import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Ban,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Filter,
  Mail,
  Search,
  ShieldCheck,
  Trash2,
  UserPlus,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { GrantAccessDialog } from "@/components/GrantAccessDialog";
import { InvitePersonDialog } from "@/components/InvitePersonDialog";
import { WorkplacePicker, type WorkplacePickerValue } from "@/components/WorkplacePicker";
import { EMPTY_WORKPLACE } from "@/components/workplace-picker-constants";
import { usePageTitle } from "@/lib/use-page-title";
import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import { formatLastLogin, formatLastLoginAbsolute } from "@/lib/last-login";
import { isSuperAdmin } from "@/lib/roles";
import { useRoleProfile } from "@/lib/use-role-profile";
import { cn } from "@/lib/utils";
import type {
  PeopleListResponse,
  Person,
  PersonPool,
  PersonPoolFilter,
  PosUser,
  PosUserMembership,
  RoleGrant,
  User,
} from "@tilt/admin-sdk";

/**
 * Unified user-management surface. One paginated list across the
 * admin console population and the POS application population, with
 * a consistent vocabulary the user can read at a glance:
 *
 *   - "Apps" column instead of "Pool" - operators don't think in
 *     pool names; they think "what apps does this person sign in
 *     to?" Future apps slot in by adding a new badge label.
 *   - Workplace label is consistent regardless of pool: just the
 *     workplace name. Partner / organization / location distinctions
 *     show up in the inline detail (memberships table) where they
 *     matter, not on the row summary.
 *
 * Per-pool counts come from the API's ``by_pool`` field; we wire it
 * straight off the query data so the chips stay accurate as
 * filters change.
 */

const PAGE_LIMIT = 50;
const SEARCH_DEBOUNCE_MS = 250;

const APP_LABEL: Record<PersonPool, string> = {
  admin: "Admin console",
  pos: "POS",
};

export function PeoplePage() {
  usePageTitle("People");
  const queryClient = useQueryClient();
  const profile = useRoleProfile();
  const callerIsSuper = isSuperAdmin(profile);
  const [poolFilter, setPoolFilter] = useState<PersonPoolFilter>("all");
  const [includeDisabled, setIncludeDisabled] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [granting, setGranting] = useState<Person | null>(null);
  const [inviting, setInviting] = useState(false);
  // Hierarchy filter - partner / org / location narrows the table to
  // people with a membership at that depth or below. Backed by the
  // same WorkplacePicker the Grant + Invite dialogs use so the
  // operator only learns one component.
  const [workplaceFilter, setWorkplaceFilter] = useState<WorkplacePickerValue>(EMPTY_WORKPLACE);

  // Debounce the search box so we don't flood the API while the user
  // types; matches the 250ms admin-portal default.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to page 1 whenever a filter or search changes - staying on
  // page 5 of a now-2-page result is just an empty state in disguise.
  useEffect(() => {
    setPageIndex(0);
  }, [debouncedSearch, poolFilter, includeDisabled, workplaceFilter.id, workplaceFilter.kind]);

  const offset = pageIndex * PAGE_LIMIT;

  // Translate the workplace selection into the right backend filter.
  // The /admin/v1/people endpoint takes one of partner_id / org_id /
  // location_id and applies a membership-EXISTS predicate.
  const wpFilterParams: Record<string, string> = {};
  if (workplaceFilter.id && workplaceFilter.kind) {
    if (workplaceFilter.kind === "partner") wpFilterParams.partner_id = workplaceFilter.id;
    else if (workplaceFilter.kind === "organization")
      wpFilterParams.organization_id = workplaceFilter.id;
    else if (workplaceFilter.kind === "location") wpFilterParams.location_id = workplaceFilter.id;
  }

  const query = useQuery<PeopleListResponse>({
    queryKey: [
      "people",
      { poolFilter, includeDisabled, debouncedSearch, offset, wp: wpFilterParams },
    ],
    queryFn: () =>
      apiClient.listPeople({
        limit: PAGE_LIMIT,
        offset,
        ...(debouncedSearch ? { q: debouncedSearch } : {}),
        pool: poolFilter,
        ...(includeDisabled ? { include_disabled: true } : {}),
        ...wpFilterParams,
      }),
    placeholderData: keepPreviousData,
  });

  const data = query.data;
  const items: Person[] = data?.people ?? [];
  const total = data?.total ?? 0;
  const byPool = data?.by_pool ?? { admin: 0, pos: 0 };
  const totalAcrossPools = byPool.admin + byPool.pos;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_LIMIT));
  const hasPrev = pageIndex > 0;
  const hasNext = pageIndex + 1 < pageCount;
  const showingStart = total === 0 ? 0 : offset + 1;
  const showingEnd = offset + items.length;

  const onMutated = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["people"] });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">People</h1>
          <p className="text-sm text-muted-foreground">
            Everyone with access to the admin console or the POS application inside your reach.
            Search across both, manage status, and grant new role memberships.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setInviting(true)}
          className="flex flex-shrink-0 items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          <UserPlus className="h-4 w-4" />
          Add person
        </button>
      </div>

      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <label className="relative flex-1 sm:max-w-sm">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email across both apps…"
              className="w-full rounded-md border bg-background py-1.5 pl-8 pr-3 text-sm"
            />
          </label>

          <div className="flex items-center gap-1 rounded-md border bg-background p-1 text-xs">
            <PoolChip
              label="All"
              count={totalAcrossPools}
              active={poolFilter === "all"}
              onClick={() => setPoolFilter("all")}
            />
            <PoolChip
              label="Admin console"
              count={byPool.admin}
              active={poolFilter === "admin"}
              onClick={() => setPoolFilter("admin")}
            />
            <PoolChip
              label="POS"
              count={byPool.pos}
              active={poolFilter === "pos"}
              onClick={() => setPoolFilter("pos")}
            />
          </div>

          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={includeDisabled}
              onChange={(e) => setIncludeDisabled(e.target.checked)}
            />
            Include disabled
          </label>

          <WorkplaceFilterButton value={workplaceFilter} onChange={setWorkplaceFilter} />

          {query.isFetching && !query.isPending && (
            <span className="text-xs text-muted-foreground" aria-live="polite">
              Updating…
            </span>
          )}
        </div>

        {query.isError && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            Failed to load people: {describe(query.error)}
          </div>
        )}
        {query.isPending ? (
          <p className="rounded-lg border border-dashed bg-muted/20 p-5 text-center text-sm text-muted-foreground">
            Loading people…
          </p>
        ) : items.length === 0 ? (
          <div className="rounded-lg border border-dashed bg-muted/40 p-6 text-center text-sm text-muted-foreground">
            {debouncedSearch
              ? `No people match "${debouncedSearch}".`
              : "No people in scope yet. Use Add person to invite someone."}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border bg-card">
            <table className="w-full text-sm">
              <thead className="sticky top-0 border-b bg-muted/40 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="w-8 px-2 py-2.5" aria-label="Expand" />
                  <th className="px-4 py-2.5">Person</th>
                  <th className="px-4 py-2.5">Apps</th>
                  <th className="px-4 py-2.5">Workplace</th>
                  <th className="px-4 py-2.5">Roles</th>
                  <th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5">Last login</th>
                  <th className="px-4 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <PersonRow
                    key={`${p.pool}:${p.id}`}
                    person={p}
                    callerUserId={profile?.user_id}
                    onMutated={onMutated}
                    onGrant={() => setGranting(p)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3 text-xs text-muted-foreground">
          <span>
            {query.isPending
              ? "Loading…"
              : total === 0
                ? "No people found"
                : `Showing ${showingStart}–${showingEnd} of ${total} people`}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
              disabled={!hasPrev || query.isPending}
              className="flex items-center gap-1 rounded-md border bg-background px-2 py-1 hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronLeft className="h-3 w-3" /> Prev
            </button>
            <span>{query.isPending ? "Page …" : `Page ${pageIndex + 1} of ${pageCount}`}</span>
            <button
              type="button"
              onClick={() => setPageIndex((i) => i + 1)}
              disabled={!hasNext || query.isPending}
              className="flex items-center gap-1 rounded-md border bg-background px-2 py-1 hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next <ChevronRight className="h-3 w-3" />
            </button>
          </div>
        </div>
      </div>

      {granting && (
        <GrantAccessDialog
          person={granting}
          onClose={() => setGranting(null)}
          onCreated={() => {
            onMutated();
            setGranting(null);
          }}
        />
      )}

      {inviting && (
        <InvitePersonDialog
          callerIsSuperAdmin={callerIsSuper}
          onClose={() => setInviting(false)}
          onCreated={() => {
            onMutated();
            setInviting(false);
          }}
        />
      )}
    </div>
  );
}

function WorkplaceFilterButton({
  value,
  onChange,
}: {
  readonly value: WorkplacePickerValue;
  readonly onChange: (next: WorkplacePickerValue) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Click-outside to close. We rely on the trigger + popover both
  // living inside ``wrapRef`` so any pointerdown elsewhere collapses
  // the panel.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && e.target instanceof Node && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // Compact label - the active selection's friendly name when set,
  // "Workplace" otherwise. The kind hint after the dot is helpful when
  // partner/org/location share a name fragment.
  const activeLabel = (() => {
    if (!value.id || !value.kind) return null;
    const name =
      (value.kind === "partner" && value.labels.partner) ||
      (value.kind === "organization" && value.labels.organization) ||
      (value.kind === "location" && value.labels.location) ||
      value.id;
    return { name, kind: value.kind };
  })();

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "flex items-center gap-1.5 rounded-md border bg-background px-2.5 py-1.5 text-xs hover:bg-muted",
          activeLabel && "border-slate-900",
        )}
      >
        <Filter className="h-3 w-3" />
        {activeLabel ? (
          <>
            <span className="font-medium text-slate-900">{activeLabel.name}</span>
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              {activeLabel.kind}
            </span>
          </>
        ) : (
          <span>Workplace</span>
        )}
        {activeLabel ? (
          <span
            role="button"
            tabIndex={0}
            aria-label="Clear workplace filter"
            onClick={(e) => {
              e.stopPropagation();
              onChange(EMPTY_WORKPLACE);
              setOpen(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onChange(EMPTY_WORKPLACE);
                setOpen(false);
              }
            }}
            className="ml-0.5 rounded-sm p-0.5 hover:bg-slate-200"
          >
            <X className="h-3 w-3" />
          </span>
        ) : (
          <ChevronDown className="h-3 w-3" />
        )}
      </button>
      {open && (
        <div className="absolute left-0 top-full z-20 mt-1 w-[320px] rounded-md border bg-white p-3 shadow-lg">
          <span className="mb-2 block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Filter by workplace
          </span>
          <WorkplacePicker value={value} onChange={onChange} idPrefix="people-filter" />
          <div className="mt-3 flex justify-end gap-2 border-t pt-2">
            <button
              type="button"
              onClick={() => {
                onChange(EMPTY_WORKPLACE);
                setOpen(false);
              }}
              className="rounded-md border bg-background px-2 py-1 text-xs hover:bg-muted"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-md bg-slate-900 px-2 py-1 text-xs font-medium text-white hover:bg-slate-800"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PoolChip({
  label,
  count,
  active,
  onClick,
}: {
  readonly label: string;
  readonly count: number;
  readonly active: boolean;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded px-2 py-0.5 font-medium",
        active ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100",
      )}
    >
      {label} <span className="opacity-70">({count})</span>
    </button>
  );
}

function PersonRow({
  person,
  callerUserId,
  onMutated,
  onGrant,
}: {
  readonly person: Person;
  readonly callerUserId: string | undefined;
  readonly onMutated: () => void;
  readonly onGrant: () => void;
}) {
  const fullName = [person.first_name, person.last_name].filter(Boolean).join(" ");
  const workplaceLabel = workplaceFor(person);
  const isAdmin = person.pool === "admin";
  const [expanded, setExpanded] = useState(false);
  // The caller can't disable themselves - locks out the operator from
  // their own console. Profile.user_id is the user_tbl PK, which is
  // the same UUID surfaced as person.id for admin-pool rows. POS rows
  // are never the caller in the admin console (different pool).
  const isSelf = isAdmin && !!callerUserId && callerUserId === person.id;

  const toggle = useMutation({
    mutationFn: async (): Promise<void> => {
      if (isAdmin) {
        if (person.is_disabled) await apiClient.enableUser(person.id);
        else await apiClient.disableUser(person.id);
        return;
      }
      if (person.is_disabled) await apiClient.enablePosUser(person.id);
      else await apiClient.disablePosUser(person.id);
    },
    onSuccess: onMutated,
  });

  return (
    <>
      <tr className="border-t hover:bg-muted/30">
        <td className="px-2 py-2">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="rounded p-1 text-slate-500 hover:bg-slate-100"
            aria-expanded={expanded}
            aria-label={expanded ? "Collapse details" : "Expand details"}
          >
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        </td>
        <td className="px-4 py-2">
          <div className="font-medium text-slate-900">{fullName || person.email}</div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Mail className="h-3 w-3" />
            {person.email}
          </div>
        </td>
        <td className="px-4 py-2">
          <span
            className={cn(
              "rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
              isAdmin ? "bg-indigo-100 text-indigo-800" : "bg-emerald-100 text-emerald-800",
            )}
          >
            {APP_LABEL[person.pool]}
          </span>
        </td>
        <td className="px-4 py-2 text-xs text-slate-700">{workplaceLabel}</td>
        <td className="px-4 py-2">
          <div className="flex flex-wrap gap-1">
            {person.roles_summary.length === 0 ? (
              <span className="text-xs text-muted-foreground">No roles</span>
            ) : (
              person.roles_summary.slice(0, 4).map((r) => (
                <span
                  key={r}
                  className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-700"
                >
                  {r.replace(/_/g, " ")}
                </span>
              ))
            )}
            {person.roles_summary.length > 4 && (
              <span className="text-[10px] text-muted-foreground">
                +{person.roles_summary.length - 4} more
              </span>
            )}
          </div>
        </td>
        <td className="px-4 py-2">
          {person.is_disabled ? (
            <span className="inline-flex items-center gap-1 text-xs text-amber-700">
              <Ban className="h-3 w-3" />
              Disabled
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
              <CheckCircle2 className="h-3 w-3" />
              Active
            </span>
          )}
        </td>
        <td
          className={cn(
            "px-4 py-2 text-xs",
            person.last_active_at ? "text-slate-700" : "text-muted-foreground",
          )}
          title={formatLastLoginAbsolute(person.last_active_at)}
        >
          {formatLastLogin(person.last_active_at)}
        </td>
        <td className="px-4 py-2">
          <div className="flex justify-end gap-1">
            <button
              type="button"
              onClick={onGrant}
              className="flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
              title="Grant a new role at a workplace"
            >
              <ShieldCheck className="h-3 w-3" />
              Grant
            </button>
            {isSelf ? (
              <span
                className="rounded border border-dashed border-slate-200 px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground"
                title="You can't disable your own account"
              >
                You
              </span>
            ) : (
              <button
                type="button"
                onClick={() => toggle.mutate()}
                disabled={toggle.isPending}
                className={cn(
                  "rounded border px-2 py-1 text-xs font-medium",
                  person.is_disabled
                    ? "border-slate-300 text-slate-700 hover:bg-slate-50"
                    : "border-red-300 text-red-700 hover:bg-red-50",
                  toggle.isPending && "opacity-50",
                )}
              >
                {person.is_disabled ? "Re-enable" : "Disable"}
              </button>
            )}
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="border-t bg-muted/20">
          <td colSpan={8} className="px-4 py-3">
            <PersonDetailExpansion person={person} onMutated={onMutated} />
          </td>
        </tr>
      )}
    </>
  );
}

function PersonDetailExpansion({
  person,
  onMutated,
}: {
  readonly person: Person;
  readonly onMutated: () => void;
}) {
  if (person.pool === "admin") {
    return <AdminPersonDetail person={person} />;
  }
  return <PosPersonDetail person={person} onMutated={onMutated} />;
}

function AdminPersonDetail({ person }: { readonly person: Person }) {
  const detail = useQuery({
    queryKey: ["person-detail", "admin", person.id],
    queryFn: () => apiClient.getPerson<User>("admin", person.id),
  });

  if (detail.isPending) {
    return <p className="text-xs text-muted-foreground">Loading memberships…</p>;
  }
  if (detail.isError) {
    return <p className="text-xs text-red-600">Failed to load detail: {describe(detail.error)}</p>;
  }

  const grants: RoleGrant[] = detail.data.roles ?? [];
  if (grants.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        No active memberships. Use Grant to add the first one.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Memberships
      </h4>
      <ul className="divide-y divide-slate-200 rounded border bg-card">
        {grants.map((g, idx) => (
          <li
            key={`${g.partner_id}:${g.scope_kind ?? "partner"}:${g.organization_id ?? ""}:${g.location_id ?? ""}:${g.role}:${idx}`}
            className="flex items-center justify-between gap-2 px-3 py-2 text-xs"
          >
            <div>
              <div className="font-medium text-slate-800">{g.role.replace(/_/g, " ")}</div>
              <div className="text-[11px] text-muted-foreground">{adminGrantScope(g)}</div>
            </div>
            {/* Inline revoke isn't wired for admin grants yet - admin
                membership rows don't surface their PK on the existing
                /admin/v1/users/{id} response, and DELETE
                /admin/v1/grants/{grant_id} needs that PK. Tracked as
                a follow-up; admins can revoke via the legacy /users
                page meanwhile. */}
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              read-only
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PosPersonDetail({
  person,
  onMutated,
}: {
  readonly person: Person;
  readonly onMutated: () => void;
}) {
  const qc = useQueryClient();
  const detail = useQuery({
    queryKey: ["person-detail", "pos", person.id],
    queryFn: () => apiClient.getPerson<PosUser>("pos", person.id),
  });

  const revoke = useMutation({
    mutationFn: (grantId: string) => apiClient.revokeGrant("pos", grantId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["person-detail", "pos", person.id] });
      onMutated();
    },
  });

  if (detail.isPending) {
    return <p className="text-xs text-muted-foreground">Loading memberships…</p>;
  }
  if (detail.isError) {
    return <p className="text-xs text-red-600">Failed to load detail: {describe(detail.error)}</p>;
  }

  const memberships: PosUserMembership[] = detail.data.memberships ?? [];
  if (memberships.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        No active memberships. Use Grant to add the first one.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Memberships
      </h4>
      <ul className="divide-y divide-slate-200 rounded border bg-card">
        {memberships.map((m) => (
          <li key={m.id} className="flex items-center justify-between gap-2 px-3 py-2 text-xs">
            <div>
              <div className="font-medium text-slate-800">{m.role_slug.replace(/_/g, " ")}</div>
              <div className="text-[11px] text-muted-foreground">{posGrantScope(m)}</div>
            </div>
            <button
              type="button"
              onClick={() => revoke.mutate(m.id)}
              disabled={revoke.isPending}
              className="flex items-center gap-1 rounded border border-red-300 px-2 py-1 text-[11px] font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 className="h-3 w-3" />
              Revoke
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Workplace label - consistent across pools.
 *
 *  Admin pool: the user's primary partner name. Just the name -
 *  prefixing "Partner:" felt redundant when the column header
 *  already says "Workplace" and admins always live under a partner.
 *
 *  POS pool: the anchor's partner name (partner-anchored) or the
 *  organization name (org-anchored). Same column, same shape - name
 *  only. The detail expansion shows the partner / org / location
 *  hierarchy explicitly.
 */
function workplaceFor(person: Person): string {
  if (person.pool === "admin") {
    return person.primary_partner?.name ?? "-";
  }
  if (person.anchor?.kind === "partner") {
    return person.anchor.partner_name ?? "-";
  }
  if (person.anchor?.kind === "organization") {
    return person.anchor.organization_name ?? "-";
  }
  return "-";
}

function adminGrantScope(g: RoleGrant): string {
  const kind = g.scope_kind ?? "partner";
  if (kind === "partner") return `Partner: ${g.partner_name ?? g.partner_path ?? "-"}`;
  if (kind === "organization")
    return `Organization: ${g.organization_name ?? g.organization_id ?? "-"}`;
  if (kind === "location") return `Location: ${g.location_name ?? g.location_id ?? "-"}`;
  return "-";
}

function posGrantScope(m: PosUserMembership): string {
  if (m.scope_kind === "partner") return `Partner: ${m.partner_name ?? "-"}`;
  if (m.scope_kind === "organization") return `Organization: ${m.organization_name ?? "-"}`;
  if (m.scope_kind === "location") return `Location: ${m.location_name ?? "-"}`;
  return "-";
}
